import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getRecordsIsFilteringSelector } from 'store/customObjectsRecordsPage/selectors';
import {
  getBusinessClientObject,
  getRecordOverviewChartViewAccess,
} from 'store/authentication/selectors';
import { getIsContactsFiltering } from 'store/contactsPage/selectors';
import {
  setLastGeneratedFilterIds as setLastGeneratedFilterIdsContacts,
  resetLastGeneratedFilter as resetLastGeneratedFilterContacts,
  clearFilterErrors as clearFilterErrorsContacts,
} from 'store/contactsPage/actions';
import {
  setLastGeneratedFilterIds as setLastGeneratedFilterIdsRecords,
  resetLastGeneratedFilter as resetLastGeneratedFilterRecords,
  clearFilterErrors as clearFilterErrorsRecords,
} from 'store/customObjectsRecordsPage/actions';

import Collapse from 'react-bootstrap/Collapse';
import Button from 'components/Button';
import DashboardGrid from 'components/DashboardGrid';
import GroupSelector from 'components/DashboardGrid/GroupSelector';
import useDashboardGridChildren, {
  MODES,
} from 'components/DashboardGrid/hooks/useDashboardChildren';
import {
  DashboardGridContainerBox,
  DashboardGridPageContainer,
} from 'components/DashboardGrid/styles';
import { getDataQAForInput } from 'components/Inputs/helpers';
import Loader from 'components/Kizen/Loader';
import { PageContentContainer } from 'components/Layout/PageContentWidth';
import PageToolbar, {
  FilterGroupSelector,
  PageSearchInput,
  PageToolbarFiltersButton,
  PageToolbarSection,
} from 'components/Layout/PageToolbar';
import { useTranslation } from 'react-i18next';
import { FilterContainer, StyledBigListTitle, StyledContainer } from './styles';
import { useHistory, useParams } from 'react-router-dom';
import { PageToolbarTitle } from './PageToolbarTitle';
import ChartModal from './modal';
import BlockModal from 'pages/Homepages/components/PluginBlock/modal';
import { HomepageBuilder } from 'ts-components/StaticBlockBuilder/HomepageBuilder';
import useModal from 'components/Modals/useModal';
import ResettableDashlet from 'pages/Dashboard/components/Dashlet/Resettable';
import useDashboardInteraction from 'components/DashboardGrid/hooks/useDashboardInteraction';
import {
  useStaticContentBlock,
  isStaticContentDashlet,
} from 'components/DashboardGrid/hooks/useStaticContentBlock';
import {
  usePluginContentBlock,
  isPluginContentDashlet,
} from 'components/DashboardGrid/hooks/usePluginContentBlock';
import { useCustomCodeBlock } from 'components/DashboardGrid/hooks/useCustomCodeBlock';
import { isCustomCodeDashlet } from 'components/DashboardGrid/hooks/customCodeBlock';
import { CustomCodeBlockEditor } from 'pages/Homepages/components/CustomCodeBlock/CustomCodeBlockEditor';
import ClientFilterDropdown2 from 'pages/Common/components/ClientFilterDropdown2';
import ChartPermission from './ChartPermission';
import { AREA_RESPONSES } from 'components/Wizards/RTDV/types';
import { QuickFilters } from 'components/Filters/QuickFilters';
import { DataManagerWrapper } from 'pages/Dashboard/context/dataManager/context';
import { useResetAll } from 'pages/Dashboard/context/dataManager/getters';
import { DashboardStateWrapper } from 'pages/Dashboard/context/persistentState';
import { useClearFilter } from 'hooks/useClearFilter';
import { ErrorMessage } from 'components/ErrorMessage';
import { DashboardWarningText } from 'pages/Dashboard/styles';
import getDashboardUrl from 'utility/getDashboardUrl';
import { deferExecution } from 'utility/defer';
import { toastVariant, useToast } from 'components/ToastProvider';
import { editableDashlet } from 'pages/Dashboard/util';
import { getDashletWithMergedStyles } from 'ts-components/StyleSettings/utils';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { FLOW_TYPE, GUARD_RAIL_CODES } from 'ts-components/Conversation';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
import {
  OverflowPortalContext,
  PortalContainer,
} from 'components/OverflowPortal';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import PluginBlockWizard from 'pages/Homepages/components/PluginBlock/PluginBlockWizard';
import { useAiBlockSession } from 'ts-components/AiBlockPanel/useAiBlockSession';
import { AI_BLOCK_TYPE } from 'ts-components/AiBlockPanel/aiBlockSession';
import { PiChatSideLayout } from 'ts-components/PiChat/PiChatSideLayout';

const NoChartsMessage = () => {
  const { t } = useTranslation();

  return (
    <ErrorMessage
      title={t('No Charts Available')}
      text={
        <DashboardWarningText as="p">
          {t(
            'There are currently no chart groups that you have access to in this business. To get started, please click the Manage Charts Groups button at the top right of the screen'
          )}
        </DashboardWarningText>
      }
      dashboard
    />
  );
};

const ObjectCharts = ({
  mobile,
  model,
  groupId,
  filterGroup,
  groups,
  handleChangeGroup,
  filterInfo,
  hasFilterError,
  aiFilterPrompt,
  clearAiFilterPrompt,
  generatedFilter,
  clearGeneratedFilter,
  openMenu,
  search,
  onChangeSearch,
  recordsCountAndNew,
  chartGroupConfigProps,
  fullFilterObject,
  toggleMenu,
  quickFilters,
  quickFilterConfig,
  allFields,
  applyFiltersAction,
  applyQuickFilters,
  saveGroupAction,
  deleteGroupAction,
  setFilterName,
  setFilterByConfig,
  clearFilter,
  preReleaseFeatures,
  updateFilterCount,
  errorsReturned,
  isLoadingView,
  onResetFilter,
}) => {
  const objectId = model?.id;
  const isForContacts = model?.fetchUrl === 'client';
  const {
    refetchDashboard,
    currentDashboard,
    currentDashboardPermission,
    dashboardQueryKey,
    persistentDashboardState,
    setPersistentDashboardState,
    setCurrentDashboardId,
    dashboards,
    refetchChartGroups,
  } = chartGroupConfigProps;

  const { t } = useTranslation();
  const [overflowPortalContainer, setOverflowPortalContainer] = useState(null);
  const history = useHistory();
  const [showToast] = useToast();
  const dispatch = useDispatch();

  const resetAll = useResetAll();
  const [currentDashlet, setCurrentDashlet] = useState({});
  const [overrideShowDashletMenu, setOverrideShowDashletMenu] = useState(false);
  const [autoOpenAIOverlay, setAutoOpenAIOverlay] = useState(false);
  const [pluginEditDashlet, setPluginEditDashlet] = useState(null);

  const customObjectEditPermission = useSelector(({ authentication }) =>
    isForContacts
      ? authentication.access.contacts?.record_overview
          ?.record_overview_chart_view?.edit
      : authentication.access.custom_objects.custom_object_entities[objectId]
          ?.record_overview?.record_overview_chart_view?.edit
  );
  const clientObject = useSelector(getBusinessClientObject);
  const isFiltering = useSelector(
    isForContacts ? getIsContactsFiltering : getRecordsIsFilteringSelector
  );
  const clientObjectId = clientObject?.id;
  const { aiFeaturesEnabled: chartGroupAiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.chart_group_builder
  );

  const { aiFeaturesEnabled: dashletAiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.dashlet_builder
  );

  const handleClickChartGroupSettings = useCallback(() => {
    if (isForContacts) {
      history.push('/clients/charts/settings');
    } else {
      history.push(`/custom-objects/${objectId}/charts/settings`);
    }
  }, [history, isForContacts, objectId]);

  const chartViewAccess = useSelector(
    getRecordOverviewChartViewAccess(!isForContacts && objectId)
  );

  const {
    renderFocusSlot: renderAiFocusSlot,
    openCreate: openAiBlockCreate,
    openEdit: openAiBlockEdit,
    sessionDashletId: aiSessionDashletId,
    panel: aiBlockPanel,
  } = useAiBlockSession({
    targetType: 'chart_group',
    dashboardId: currentDashboard?.id,
    objectId,
    dashlets: currentDashboard?.dashlets,
    dashboardName: currentDashboard?.name,
    objectName: model?.entityName,
  });

  const {
    breakpoint,
    setBreakpoint,
    editable,
    rowHeight,
    setContainerWidth,
    dashlets,
    hasEditPermission,
    resizeHandles,
    updateLayouts,
    mutateDashlet,
    forceShowUIElements,
    deleteDashlet,
    draggable,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  } = useDashboardInteraction({
    currentPermission: currentDashboardPermission,
    refetchDashboard,
    entityName: t('Chart'),
    dashboardQueryKey,
    currentDashboard,
    isLoading: false,
    canEdit: chartViewAccess.edit,
    aiSessionDashletId,
  });

  const {
    mutateStaticContentBlock,
    openStaticContentBuilder,
    staticContentBuilderOpen,
    staticContentBuilderProps,
  } = useStaticContentBlock({
    currentDashboard,
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutatePluginBlock } = usePluginContentBlock({
    dashlets,
    getDashletPosition,
    createDashletMutation,
    updateDashletMutation,
    duplicateDashlet,
  });

  const { mutateCustomCodeBlock, openCustomCodeEditor, customCodeEditorProps } =
    useCustomCodeBlock({
      currentDashboard,
      dashlets,
      updateDashletMutation,
      duplicateDashlet,
    });

  const [addDashletModalProps, addDashletModalTriggerProps] = useModal({
    handleSubmit: (chart, builderData) => {
      if (!chart) {
        return mutateStaticContentBlock({
          dashboardId: currentDashboard.id,
          builderData,
        });
      }
      if (chart?.type === AI_BLOCK_TYPE) {
        // The AI sidebar creates the chart on the agent's first push
        openAiBlockCreate();
        return;
      }
      if (chart?.type === 'plugin_block') {
        return mutatePluginBlock({
          dashboardId: currentDashboard.id,
          result: chart,
        });
      }
      return mutateDashlet({
        dashletData: chart,
        customObject: objectId,
        isUpdate: false,
      });
    },
  });

  const [
    editDashletModalProps,
    ,
    { show: handleShowEditDashlet, hide: handleHideEditDashlet },
  ] = useModal({
    handleSubmit: (chart) => {
      setCurrentDashlet({});
      return mutateDashlet({
        dashletData: { ...chart, id: currentDashlet.id },
        isUpdate: true,
      });
    },
  });

  const handleEditDashlet = useCallback(
    (newDashlet, oldDashlet, isTableColumnUpdate) => {
      return mutateDashlet({
        dashletData: { ...newDashlet, id: oldDashlet.id },
        isUpdate: true,
        customObject: newDashlet.homepageSelectedObjectId,
        layoutUpdate: isTableColumnUpdate,
      });
    },
    [mutateDashlet]
  );

  const renderChild = useCallback(
    ({ dashlet, index }) => {
      return renderAiFocusSlot(dashlet, draggable && hasEditPermission, () => (
        <ChartPermission
          dashlet={dashlet}
          model={model}
          ModalComponent={ChartModal}
          isDraggable={draggable && hasEditPermission}
          onDelete={deleteDashlet}
          canEdit={hasEditPermission}
          editDashlet={(newDashlet, _payload, isTableColumnUpdate) =>
            handleEditDashlet(newDashlet, dashlet, isTableColumnUpdate)
          }
        >
          <ResettableDashlet
            deleteDashlet={deleteDashlet}
            currentDashboard={currentDashboard}
            dashlet={getDashletWithMergedStyles(dashlet, currentDashboard)}
            onDeleteDashlet={deleteDashlet}
            onShowEditModal={() => {
              setAutoOpenAIOverlay(false);
              // An AI block with a stored session opens the AI sidebar in
              // focus mode; without one (or an unparseable one) it falls
              // through to the JSON editor below
              if (openAiBlockEdit(dashlet)) {
                return;
              }
              if (isCustomCodeDashlet(dashlet)) {
                openCustomCodeEditor(dashlet);
                return;
              }
              if (isStaticContentDashlet(dashlet)) {
                openStaticContentBuilder(dashlet);
                return;
              }
              if (isPluginContentDashlet(dashlet)) {
                setPluginEditDashlet(dashlet);
                return;
              }
              setCurrentDashlet(dashlet);
              deferExecution(() => handleShowEditDashlet());
            }}
            isDraggable={draggable && hasEditPermission}
            editDashlet={(newDashlet, builderData, isTableColumnUpdate) => {
              if (
                builderData &&
                typeof builderData === 'object' &&
                !newDashlet
              ) {
                return mutateStaticContentBlock({
                  dashboardId: currentDashboard.id,
                  builderData,
                  dashletId: dashlet.id,
                });
              }
              return handleEditDashlet(
                newDashlet,
                dashlet,
                isTableColumnUpdate
              );
            }}
            duplicateDashlet={(dashletData) => {
              if (isCustomCodeDashlet(dashlet)) {
                return mutateCustomCodeBlock({
                  dashboardId: currentDashboard.id,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                  name: dashletData.name,
                });
              }
              if (isStaticContentDashlet(dashlet)) {
                return mutateStaticContentBlock({
                  dashboardId: currentDashboard.id,
                  builderData: dashlet.config.content,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                });
              }
              if (isPluginContentDashlet(dashlet)) {
                return mutatePluginBlock({
                  dashboardId: currentDashboard.id,
                  dashletId: dashlet.id,
                  isDuplicate: true,
                  name: dashletData.name,
                });
              }
              return mutateDashlet({
                dashletData: { ...dashletData, id: dashlet.id },
                isDuplicate: true,
              });
            }}
            onEditWithAIHandler={
              dashletAiFeaturesEnabled && !isStaticContentDashlet(dashlet)
                ? () => {
                    setCurrentDashlet(dashlet);
                    deferExecution(() => {
                      handleShowEditDashlet();
                      setAutoOpenAIOverlay(true);
                    });
                  }
                : undefined
            }
            setOverrideShowDashletMenu={setOverrideShowDashletMenu}
            canEdit={hasEditPermission}
            dashboardId={currentDashboard.id}
            breakpoint={breakpoint}
            mobile={mobile}
            model={model}
            ModalComponent={ChartModal}
            search={search}
            filtersForCharts={fullFilterObject}
            objectForCharts={model}
            charts
            index={index}
            aiSessionDashletId={aiSessionDashletId}
          />
        </ChartPermission>
      ));
    },
    [
      renderAiFocusSlot,
      aiSessionDashletId,
      openAiBlockEdit,
      dashletAiFeaturesEnabled,
      currentDashboard,
      handleEditDashlet,
      hasEditPermission,
      breakpoint,
      mobile,
      mutateDashlet,
      mutatePluginBlock,
      mutateStaticContentBlock,
      mutateCustomCodeBlock,
      openStaticContentBuilder,
      openCustomCodeEditor,
      deleteDashlet,
      model,
      search,
      fullFilterObject,
      draggable,
      handleShowEditDashlet,
    ]
  );

  const filteredDashlets = useMemo(() => {
    return dashlets.filter((dashlet) => {
      // The table chart is special and does not render on mobile, so we
      // want to remove it from the list before we try to render any dashlets
      if (mobile && dashlet.config.reportType === AREA_RESPONSES.TABLE) {
        return false;
      }
      return true;
    });
  }, [dashlets, mobile]);

  const filteredDashboards = useMemo(
    () => dashboards.filter((d) => !d.hidden),
    [dashboards]
  );

  const hasDashboards = useMemo(() => {
    return filteredDashboards.length > 0;
  }, [filteredDashboards]);

  const children = useDashboardGridChildren({
    renderChild,
    forceShowUIElements,
    dashlets: filteredDashlets,
    defaultStyleSettings: currentDashboard?.styleSettings,
    hasEditPermission,
    breakpoint,
    addDashletModalTriggerProps,
    mobile,
    dateFilter: {}, // no global date filters for charts
    mode: MODES.CHARTS,
    filtersForCharts: fullFilterObject,
  });

  const toggle = () => {
    toggleMenu((ps) => !ps);
  };

  const availableIds = useMemo(() => {
    return chartGroupConfigProps?.dashboards?.map(({ id }) => id) ?? [];
  }, [chartGroupConfigProps]);

  const redirectToGeneratedChartGroup = useCallback(
    async (chartGroupPath) => {
      await refetchChartGroups?.();
      deferExecution(() => {
        history.push(chartGroupPath);
      });
    },
    [history, refetchChartGroups]
  );

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.chart_groups?.guardrail_code;
    const guardrailMessage = runMetadata?.chart_groups?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedChartGroup =
      Object.keys(runMetadata?.chart_groups || {}).length > 0
        ? Object.values(runMetadata?.chart_groups)?.[0]
        : null;

    return {
      hasResult: !!generatedChartGroup,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const onGenerationSuccess = useCallback(
    (homepageFlowStatusData) => {
      const basePath = isForContacts
        ? '/clients/charts'
        : `/custom-objects/${objectId}/charts`;

      const navigateUrl = homepageFlowStatusData?.next_actions?.[0]?.url;
      const guardrailCode = homepageFlowStatusData.run_metadata?.guardrail_code;
      const guardrailMessage =
        homepageFlowStatusData.run_metadata?.guardrail_message;
      if (navigateUrl) {
        try {
          const url = new URL(navigateUrl);
          const relativePath = url.pathname + url.search;
          if (guardrailCode && guardrailMessage) {
            showToast({
              message: guardrailMessage,
              variant:
                guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                  ? toastVariant.ALERT
                  : toastVariant.FAILURE,
              autohide: false,
            });
          } else {
            showToast({
              message: t('Chart Group Generated Successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          setTimeout(() => {
            redirectToGeneratedChartGroup(relativePath);
          }, 1500);
          return;
        } catch (error) {
          monitoringExceptionHelper(error, {
            extra: {
              nextActionUrl: navigateUrl,
            },
          });
        }
      }

      // Handle one-shot response with run_metadata
      const generatedChartGroup =
        Object.keys(homepageFlowStatusData.run_metadata?.chart_groups || {})
          .length > 0
          ? Object.values(
              homepageFlowStatusData.run_metadata?.chart_groups
            )?.[0]
          : null;

      if (guardrailCode && guardrailMessage) {
        showToast({
          message: guardrailMessage,
          variant:
            guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
              ? toastVariant.ALERT
              : toastVariant.FAILURE,
          autohide: false,
        });
        if (generatedChartGroup) {
          setTimeout(() => {
            redirectToGeneratedChartGroup(
              `${basePath}/${generatedChartGroup.id}`
            );
          }, 1500);
        }
      } else if (generatedChartGroup) {
        showToast({
          message: t('Chart Group Generated Successfully'),
          variant: toastVariant.SUCCESS,
        });
        setTimeout(() => {
          redirectToGeneratedChartGroup(
            `${basePath}/${generatedChartGroup.id}`
          );
        }, 1500);
      } else {
        showToast({
          message: t('Chart Group Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, isForContacts, objectId, t, redirectToGeneratedChartGroup]
  );

  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Chart Group Generation Failed, Please try again later'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t]
  );

  const managedHandleChangeGroup = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      handleChangeGroup?.(...args);
    },
    [handleChangeGroup, resetAll, availableIds]
  );

  const managedHandleChangeSearch = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      onChangeSearch?.(...args);
    },
    [resetAll, onChangeSearch, availableIds]
  );

  const managedApplyFilters = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      applyFiltersAction?.(...args);
    },
    [applyFiltersAction, resetAll, availableIds]
  );

  const managedSaveGroup = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      saveGroupAction?.(...args);
    },
    [saveGroupAction, resetAll, availableIds]
  );

  const managedDeleteGroup = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      deleteGroupAction?.(...args);
    },
    [deleteGroupAction, resetAll, availableIds]
  );

  const handleClearFilter = useClearFilter(clearFilter, toggleMenu);

  const managedClearFilter = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      handleClearFilter?.(...args);
    },
    [handleClearFilter, resetAll, availableIds]
  );

  const managedApplyQuickFilters = useCallback(
    (...args) => {
      resetAll?.(availableIds);
      applyQuickFilters?.(...args);
    },
    [resetAll, applyQuickFilters, availableIds]
  );

  const handleSetLastGeneratedFilterIds = useCallback(
    (data) => {
      if (isForContacts) {
        dispatch(setLastGeneratedFilterIdsContacts(data));
      } else {
        dispatch(setLastGeneratedFilterIdsRecords(data));
      }
    },
    [dispatch, isForContacts]
  );

  const handleResetLastGeneratedFilter = useCallback(() => {
    if (isForContacts) {
      dispatch(resetLastGeneratedFilterContacts());
    } else {
      dispatch(resetLastGeneratedFilterRecords());
    }
  }, [dispatch, isForContacts]);

  const handleClearFilterErrors = useCallback(() => {
    if (isForContacts) {
      dispatch(clearFilterErrorsContacts());
    } else {
      dispatch(clearFilterErrorsRecords());
    }
  }, [dispatch, isForContacts]);

  const previousGroupId = useRef(groupId);
  const previousSearch = useRef(search);

  const hasNoDashboards = useMemo(
    () => !isLoadingView && filteredDashboards.length === 0,
    [isLoadingView, filteredDashboards]
  );

  const currentDashletWithDefaultStyles = useMemo(
    () => getDashletWithMergedStyles(currentDashlet, currentDashboard),
    [currentDashlet, currentDashboard]
  );

  // Mobile behaves differently than desktop, and the filters are not
  // controlled by the data manager. We also only have filter groups available,
  // so only need to trigger a reset when the groupId or the search term changes
  useEffect(() => {
    if (
      mobile &&
      (previousGroupId.current !== groupId || previousSearch.current !== search)
    ) {
      resetAll?.(availableIds);
      previousGroupId.current = groupId;
      previousSearch.current = search;
    }
  }, [groupId, mobile, search, resetAll, availableIds]);

  if (!model) {
    return <Loader loading />;
  }

  if (
    currentDashboard?.type === 'generic_dashboard' ||
    currentDashboard?.type === 'homepage' ||
    (currentDashboard === null && hasDashboards) ||
    (currentDashboard?.customObject &&
      currentDashboard?.customObject !== objectId)
  ) {
    const fallBackDashboardId = filteredDashboards[0]?.id;
    if (fallBackDashboardId) {
      showToast({
        message: t(
          'The chart group you tried to view is not accessible to you. Contact your administrator if you think this is in error'
        ),
        variant: toastVariant.FAILURE,
      });
      setCurrentDashboardId(fallBackDashboardId);
    }
    const clientId =
      clientObjectId === currentDashboard?.customObject ? clientObjectId : null;
    deferExecution(() =>
      history.push(
        getDashboardUrl(
          currentDashboard,
          currentDashboard?.customObject,
          clientId
        )
      )
    );
  }

  return (
    <>
      <OverflowPortalContext.Provider value={overflowPortalContainer}>
        <DashboardStateWrapper
          setPersistentDashboardState={setPersistentDashboardState}
          persistentDashboardState={persistentDashboardState}
        >
          <DashboardGridPageContainer isMobile={mobile}>
            {!mobile ? (
              <PageContentContainer>
                <PageToolbar>
                  <PageToolbarSection>
                    <FilterGroupSelector
                      value={groupId || 'none'}
                      options={groups}
                      onChange={managedHandleChangeGroup}
                      title={t('Change Group')}
                      link={
                        isForContacts
                          ? '/clients/filter-groups'
                          : `/custom-objects/${objectId}/filter-groups`
                      }
                    />
                    <PageToolbarFiltersButton
                      badge={filterInfo.numberOfFilters}
                      showBackground={openMenu}
                      onClick={toggle}
                      errors={hasFilterError}
                    />
                    <PageSearchInput
                      placeholder={
                        isForContacts
                          ? `${t('Find Contacts')}`
                          : `${t('Find')} ${model.entityName} ${t('Records')}`
                      }
                      value={search}
                      onChange={managedHandleChangeSearch}
                      isClearable
                      loading={isFiltering}
                      {...getDataQAForInput('search-co-records', 'search')}
                    />
                  </PageToolbarSection>
                  <PageToolbarTitle
                    data-qa="header-record-count"
                    count={recordsCountAndNew}
                    single={
                      isForContacts
                        ? t('Contact')
                        : `${model.entityName} ${t('Record')}`
                    }
                    plural={
                      isForContacts
                        ? t('Contacts')
                        : `${model.entityName} ${t('Records')}`
                    }
                  />
                  <PageToolbarSection fixed>
                    {chartGroupAiFeaturesEnabled &&
                    customObjectEditPermission ? (
                      <AiExpandableSinglePrompt
                        flowType={FLOW_TYPE.chart_group_builder_inline}
                        taskType="dashboard_plan_generation"
                        customObjectId={objectId}
                        builderScope="charts"
                        context={{}}
                        onOneShotSuccess={onGenerationSuccess}
                        onChatSessionSuccess={onGenerationSuccess}
                        onGenerationFailure={onGenerationFailure}
                        entityName={t('Chart Group')}
                        extractGenerationResult={extractGenerationResult}
                        messagesConfig={{
                          placeholder: t('Create a chart group using AI'),
                          buildingMessage: t('Building your chart group'),
                          successMessage: t(
                            'Chart Group Generated Successfully'
                          ),
                          failureMessage: t(
                            'Chart Group Generation Failed, Please try again later'
                          ),
                          expandedPlaceholder: t('Add additional context'),
                          closingMessage: t('Closing session'),
                          initialMessage: t(
                            'What type of chart group would you like to create?'
                          ),
                        }}
                        importResults={true}
                        popoverProps={{
                          placement: 'bottom-end',
                        }}
                      />
                    ) : null}
                    <Button
                      color="blue"
                      onClick={handleClickChartGroupSettings}
                    >
                      {t('Manage Chart Groups')}
                    </Button>
                    {hasEditPermission && hasDashboards ? (
                      <Button {...addDashletModalTriggerProps}>
                        {t('Add Chart')}
                      </Button>
                    ) : null}
                  </PageToolbarSection>
                </PageToolbar>
                <FilterContainer>
                  <Collapse mountOnEnter in={openMenu}>
                    <ClientFilterDropdown2
                      key={
                        // the truthy value of isAllContactsGroup is a UUID
                        filterInfo.isAllContactsGroup || filterInfo.key
                      }
                      hasFilterError={hasFilterError}
                      filterData={filterInfo.config}
                      and={filterInfo.and}
                      applyFiltersAction={managedApplyFilters}
                      aiFilterPrompt={aiFilterPrompt}
                      clearAiFilterPrompt={clearAiFilterPrompt}
                      generatedFilter={generatedFilter}
                      clearGeneratedFilter={clearGeneratedFilter}
                      saveGroupAction={managedSaveGroup}
                      deleteGroupAction={managedDeleteGroup}
                      filterName={filterInfo.name}
                      setFilterName={setFilterName}
                      setFilterByConfig={setFilterByConfig}
                      updateFilterCount={updateFilterCount}
                      numberOfFilters={filterInfo.numberOfFilters}
                      handleChangeGroup={managedHandleChangeGroup}
                      groupId={groupId}
                      filterGroup={filterGroup}
                      clearFilter={managedClearFilter}
                      betaPreview={preReleaseFeatures}
                      objectType={
                        isForContacts ? 'client_client' : model.objectType
                      }
                      customObjectName={model?.entityName}
                      errorsReturned={errorsReturned}
                      customObjectId={model?.id}
                      onResetFilter={onResetFilter}
                      setLastGeneratedFilterIds={
                        handleSetLastGeneratedFilterIds
                      }
                      resetLastGeneratedFilter={handleResetLastGeneratedFilter}
                      clearFilterErrors={handleClearFilterErrors}
                    />
                  </Collapse>
                </FilterContainer>
                {quickFilters ? (
                  <QuickFilters
                    quickFilters={quickFilters}
                    quickFilterConfig={quickFilterConfig}
                    fullFilterObject={fullFilterObject}
                    search={search}
                    resultsCount={recordsCountAndNew}
                    model={model}
                    fields={allFields}
                    onChange={managedApplyQuickFilters}
                    errors={filterInfo?.errors?.quickFiltersErrors}
                    hasFilterErrors={filterInfo?.errors}
                  />
                ) : null}
              </PageContentContainer>
            ) : (
              <>
                <StyledBigListTitle
                  count={recordsCountAndNew}
                  single={
                    isForContacts
                      ? t('Contact')
                      : `${model.entityName} ${t('Record')}`
                  }
                  plural={
                    isForContacts
                      ? t('Contacts')
                      : `${model.entityName} ${t('Records')}`
                  }
                />
                <GroupSelector {...chartGroupConfigProps} mobile />
              </>
            )}
            {/* Outside the Loader, so a view reload doesn't remount the
                panel (a remount starts a new AI session) */}
            <PiChatSideLayout panel={aiBlockPanel}>
              {((!currentDashboard || currentDashboard.error) &&
                !isLoadingView) ||
              hasNoDashboards ? (
                <NoChartsMessage />
              ) : !filterInfo?.currentErrors?.length ? (
                <Loader loading={isLoadingView}>
                  <DashboardGridContainerBox>
                    <StyledContainer
                      mobile={mobile}
                      className="breakpoint-container"
                    >
                      <DashboardGrid
                        updateLayouts={updateLayouts}
                        resizeHandles={resizeHandles}
                        rowHeight={rowHeight}
                        editable={editable}
                        setBreakpoint={setBreakpoint}
                        setContainerWidth={setContainerWidth}
                      >
                        {children}
                      </DashboardGrid>
                    </StyledContainer>
                  </DashboardGridContainerBox>
                </Loader>
              ) : (
                false
              )}
            </PiChatSideLayout>
          </DashboardGridPageContainer>
        </DashboardStateWrapper>
      </OverflowPortalContext.Provider>
      <PortalContainer ref={setOverflowPortalContainer} zIndex={1} />
      {currentDashlet?.id &&
      !hasEditPermission &&
      !editDashletModalProps.show ? null : (
        <ChartModal
          {...editDashletModalProps}
          existing={
            currentDashlet.config
              ? editableDashlet(currentDashletWithDefaultStyles, t)
              : {}
          }
          autoOpenAIOverlay={autoOpenAIOverlay}
          currentDashboard={currentDashboard}
          scrollActvitiyBuilderIntoView={overrideShowDashletMenu}
          hideModal={() => {
            handleHideEditDashlet();
            setCurrentDashlet({});
          }}
          clientObjectId={clientObjectId}
          objectId={currentDashlet?.customObject}
          contacts={clientObjectId === currentDashlet?.customObject}
          model={currentDashboard?.customObject}
        />
      )}
      <BlockModal
        {...addDashletModalProps}
        mode={MODES.CHARTS}
        currentDashboard={currentDashboard}
        objectId={objectId}
        contacts={isForContacts}
        model={model}
      />
      {staticContentBuilderOpen && (
        <HomepageBuilder {...staticContentBuilderProps} />
      )}
      {customCodeEditorProps && (
        <CustomCodeBlockEditor {...customCodeEditorProps} />
      )}
      {pluginEditDashlet && (
        <PluginBlockWizard
          dashlet={pluginEditDashlet}
          mode={MODES.CHARTS}
          onHide={() => setPluginEditDashlet(null)}
          onConfirm={(result) => {
            const id = pluginEditDashlet.id;
            setPluginEditDashlet(null);
            mutatePluginBlock({
              dashboardId: currentDashboard.id,
              dashletId: id,
              result,
            });
          }}
        />
      )}
    </>
  );
};

const ObjectChartsWithDataManager = (props) => {
  const { chartId } = useParams();

  const objectId = props.model?.id;

  if (!chartId) {
    return <ObjectCharts {...props} />;
  }

  return (
    <DataManagerWrapper key={chartId} instanceId={chartId} parentId={objectId}>
      <ObjectCharts {...props} />
    </DataManagerWrapper>
  );
};

export default ObjectChartsWithDataManager;
